import { useLocalStorage, usePreferredDark } from '@vueuse/core'
import { defineStore } from 'pinia'
import { computed, watch } from 'vue'

/** What the diner chose. `system` follows the OS and is the default. */
const PJ_THEME_CHOICE = {
  SYSTEM: 'system',
  LIGHT: 'light',
  DARK: 'dark'
} as const

type PjThemeChoice = (typeof PJ_THEME_CHOICE)[keyof typeof PJ_THEME_CHOICE]

/** What is actually on screen once `system` has been resolved. */
type PjResolvedTheme = Exclude<PjThemeChoice, typeof PJ_THEME_CHOICE.SYSTEM>

const PJ_THEME_STORAGE_KEY = 'twn-pjazza-theme'

/** Matches the --pj-bg of each theme, for the browser's own chrome. */
const THEME_COLOUR: Record<PjResolvedTheme, string> = {
  light: '#FFFFFF',
  dark: '#000000'
}

const isThemeChoice = (value: unknown): value is PjThemeChoice =>
  Object.values(PJ_THEME_CHOICE).includes(value as PjThemeChoice)

/**
 * Runs a theme change with every transition held off until it has painted.
 *
 * Controls fade their colours on hover and press, so flipping every colour at
 * once started all of those fades together and the page changed piece by piece.
 * The override goes in, the new colours resolve under it, and it comes out after
 * the next frame. The interfaces:better-ui recipe, step for step.
 */
const withoutTransitions = (change: () => void): void => {
  if (typeof requestAnimationFrame !== 'function') {
    change()

    return
  }

  const override = document.createElement('style')

  override.append(document.createTextNode('*,*::before,*::after{transition:none!important}'))
  document.head.append(override)

  change()

  // Forces the new colours to resolve while the override is still in.
  void document.body.offsetHeight

  requestAnimationFrame(() => requestAnimationFrame(() => override.remove()))
}

/**
 * Light or dark, and who decides.
 *
 * Default is `system`, so the app arrives looking like the rest of the phone and
 * nobody has to set anything. The choice is per device rather than per account:
 * it is a property of the screen being held, not of who is holding it, and it has
 * to work before anybody signs in.
 *
 * `system` removes the attribute entirely rather than resolving it to a value.
 * That hands the job back to the token file's own prefers-color-scheme block, so
 * following the OS costs no JavaScript and keeps working while the page is open —
 * which matters on a phone that switches appearance at sunset. An explicit choice
 * sets the attribute, and `:root[data-theme]` outranks the media query.
 *
 * The blade root writes the same attribute inline before first paint, so on a
 * dark-choosing device the first frame is already dark. This store agrees with
 * what is there rather than applying it late and repainting.
 */
export const usePjazzaThemeStore = defineStore('pjazza-theme', () => {
  const choice = useLocalStorage<PjThemeChoice>(PJ_THEME_STORAGE_KEY, 'system', {
    serializer: {
      read: (raw: string): PjThemeChoice => (isThemeChoice(raw) ? raw : 'system'),
      write: (value: PjThemeChoice): string => value
    },
    // Safari private mode throws on write; the theme still applies in memory.
    onError: () => undefined
  })

  // Reactive, not read once: a diner on `system` follows the OS live.
  const systemPrefersDark = usePreferredDark()

  const resolved = computed<PjResolvedTheme>(() => {
    if (choice.value === PJ_THEME_CHOICE.SYSTEM)
      return systemPrefersDark.value ? PJ_THEME_CHOICE.DARK : PJ_THEME_CHOICE.LIGHT

    return choice.value
  })

  // Also runs when the OS flips under `system`: the watch below fires on the
  // media query's change event, before the browser restyles for it.
  const apply = (): void => {
    if (typeof document === 'undefined') return

    const root = document.documentElement

    withoutTransitions(() => {
      if (choice.value === PJ_THEME_CHOICE.SYSTEM) {
        root.removeAttribute('data-theme')
      } else {
        root.setAttribute('data-theme', choice.value)
      }
    })

    // So the browser's own furniture — the address bar, the task switcher —
    // matches the page instead of framing a dark app in white.
    document.querySelector('meta[name="theme-color"]')?.setAttribute('content', THEME_COLOUR[resolved.value])
  }

  // immediate, because a stored choice has to be on the element before anything
  // reads it; flush 'sync' so a tap on the toggle repaints in the same frame
  // rather than a tick later, which reads as lag on a whole-page colour change.
  watch([choice, resolved], apply, { immediate: true, flush: 'sync' })

  const set = (next: PjThemeChoice): void => {
    choice.value = next
  }

  return { choice, resolved, systemPrefersDark, set }
})

export { isThemeChoice, PJ_THEME_STORAGE_KEY, THEME_COLOUR }
export type { PjResolvedTheme, PjThemeChoice }
